import { useState } from 'react'
import { Button, Checkbox, Drawer, Form, Input, Space, Tag, Typography, App } from 'antd'
import { PlusOutlined, EditOutlined, KeyOutlined, UndoOutlined } from '@ant-design/icons'
import { Popconfirm } from 'antd'
import apiClient from '@/shared/api/client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rolesApi } from '@/features/roles/api'
import type { Role, Permission } from '@/shared/types/rbac'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

export default function RolesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [permDrawerOpen, setPermDrawerOpen] = useState(false)
  const [permRole, setPermRole] = useState<Role | null>(null)
  const [selectedPerms, setSelectedPerms] = useState<string[]>([])

  const canCreate = usePermission('role.create')
  const canAssign = usePermission('role.assign')

  const { data, isLoading } = useQuery({
    queryKey: ['roles', page, pageSize],
    queryFn: () => rolesApi.list({ page, page_size: pageSize }),
  })

  const { data: allPerms } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => rolesApi.listPermissions(),
  })

  const openCreate = () => {
    form.resetFields()
    setDrawerOpen(true)
  }

  const openPermDrawer = (record: Role) => {
    setPermRole(record)
    setSelectedPerms(record.permissions?.map((p) => p.id) ?? [])
    setPermDrawerOpen(true)
  }

  const createMutation = useMutation({
    mutationFn: (values: { role_code: string; role_name: string; description?: string }) =>
      rolesApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      message.success('Role created')
      setDrawerOpen(false)
      form.resetFields()
    },
    onError: () => message.error('Failed to create role'),
  })

  const assignPermsMutation = useMutation({
    mutationFn: () =>
      rolesApi.assignPermissions(permRole!.id, { permission_ids: selectedPerms }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      // GOV-001 P9 — grants are now admin-owned: the nightly seed no
      // longer reverts this role to factory defaults.
      message.success('Permissions updated — this role is now Customised and will keep your changes')
      setPermDrawerOpen(false)
    },
    onError: () => message.error('Update failed'),
  })

  const resetMutation = useMutation({
    mutationFn: (roleId: string) =>
      apiClient.post(`/roles/${roleId}/reset-grants`).then((r: any) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] })
      message.success('Restored to factory permission set')
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.detail ?? 'Reset failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => createMutation.mutate(v))

  // Group permissions by entity prefix (e.g. "company", "plant", …)
  const groupedPerms = (allPerms ?? []).reduce<Record<string, Permission[]>>((acc, p) => {
    const group = p.perm_code.split('.')[0]
    if (!acc[group]) acc[group] = []
    acc[group].push(p)
    return acc
  }, {})

  const columns = [
    { title: 'Role Name', dataIndex: 'role_name', key: 'role_name' },
    { title: 'Code', dataIndex: 'role_code', key: 'role_code', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Description', dataIndex: 'description', key: 'description', render: (v?: string) => v ?? '—' },
    {
      title: 'System',
      dataIndex: 'is_system',
      key: 'is_system',
      render: (v: boolean) => <Tag color={v ? 'gold' : 'default'}>{v ? 'System' : 'Custom'}</Tag>,
    },
    {
      title: 'Grants',
      dataIndex: 'admin_modified_at',
      key: 'admin_modified_at',
      render: (v?: string) =>
        v ? <Tag color="purple">Customised</Tag> : <Tag>Factory</Tag>,
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (perms?: Permission[]) => (
        <span>{perms?.length ?? 0} permission{(perms?.length ?? 0) !== 1 ? 's' : ''}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Role) => (
        <Space>
          {canAssign && (
            <Button size="small" icon={<KeyOutlined />} onClick={() => openPermDrawer(record)}>
              Permissions
            </Button>
          )}
          {canAssign && (record as any).admin_modified_at && (
            <Popconfirm
              title="Restore factory permissions?"
              description="Removes all customisation and returns this role to the system default set."
              onConfirm={() => resetMutation.mutate(record.id)}
            >
              <Button size="small" icon={<UndoOutlined />} loading={resetMutation.isPending}>
                Reset
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Roles"
        subtitle="View roles and manage permission assignments"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Role
            </Button>
          )
        }
      />

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p, ps) => { setPage(p); setPageSize(ps) }}
      />

      {/* Create role drawer */}
      <FormDrawer
        title="New Role"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={createMutation.isPending}
        form={form}
      >
        <Form.Item
          name="role_code"
          label="Role Code"
          rules={[{ required: true }, { pattern: /^[A-Z0-9_]+$/, message: 'Uppercase letters, numbers and underscores only' }]}
        >
          <Input placeholder="e.g. QUALITY_MANAGER" style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="role_name" label="Role Name" rules={[{ required: true }]}>
          <Input placeholder="e.g. Quality Manager" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
      </FormDrawer>

      {/* Permissions assignment drawer */}
      <Drawer
        title={`Permissions — ${permRole?.role_name ?? ''}`}
        open={permDrawerOpen}
        onClose={() => setPermDrawerOpen(false)}
        width={520}
        extra={
          <Space>
            <Button onClick={() => setPermDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" loading={assignPermsMutation.isPending} onClick={() => assignPermsMutation.mutate()}>
              Save
            </Button>
          </Space>
        }
      >
        {Object.entries(groupedPerms).map(([group, perms]) => (
          <div key={group} style={{ marginBottom: 20 }}>
            <Typography.Text strong style={{ textTransform: 'capitalize', display: 'block', marginBottom: 8 }}>
              {group}
            </Typography.Text>
            <Space direction="vertical" size={4}>
              {perms.map((p) => (
                <Checkbox
                  key={p.id}
                  checked={selectedPerms.includes(p.id)}
                  onChange={(e) =>
                    setSelectedPerms(
                      e.target.checked
                        ? [...selectedPerms, p.id]
                        : selectedPerms.filter((id) => id !== p.id),
                    )
                  }
                >
                  <span>{p.perm_code}</span>
                  {p.description && (
                    <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                      {p.description}
                    </Typography.Text>
                  )}
                </Checkbox>
              ))}
            </Space>
          </div>
        ))}
      </Drawer>
    </div>
  )
}
